<template>
  <v-container fluid class="mt-n4">
    <v-row no-gutters align="center" justify="end">
      <v-col cols="auto">
        <v-row justify="end" align="center" dense>
          <v-col cols="auto">
            <TabbedColumnAdjuster
            v-if="columnSet"
            :propColumnSet="columnSet"
            :propTabs="columnAdjusterTabs"
            @new-columns-selected="columnSet = $event"/>
          </v-col>
          <v-col cols="auto" v-if="!isSpeciesView">
            <Icon
            :small="false"
            icon="mdi-pine-tree-variant"
            iconColor="primary"
            :tooltipText="$t('showSpeciesView')"
            @icon-clicked="toggleView"/>
          </v-col>
          <v-col cols="auto" v-if="isSpeciesView">
            <Icon
            :small="false"
            icon="mdi-leaf-maple"
            iconColor="primary"
            :tooltipText="$t('showProductsView')"
            @icon-clicked="toggleView"/>
          </v-col>
          <v-col cols="auto">
            <v-menu left>
              <template #activator="{ on }">
                <v-icon v-on="on" color="primary">mdi-dots-horizontal</v-icon>
              </template>
              <v-list id="tract-group-analysis-context-menu">
                <v-list-item
                v-for="(item, i) in contextMenuOptions"
                :key="`tg-analysis-context-menu-${i}`"
                @click="item.action">
                  <v-avatar left>
                    <v-icon color="black">{{ item.icon }}</v-icon>
                  </v-avatar>
                  <v-list-item-title class="subtitle-1">{{ $t(item.label) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          <v-col cols="auto">
            <Icon
            icon="mdi-refresh"
            @icon-clicked="refreshAnalysis"
            :tooltipText="$t('refresh')"
            :small="false"/>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <LoadableComponent :loading="loading" :height="300">
          <DataTable
          v-if="tableItems.length > 0"
          :items.sync="tableItems"
          :headers.sync="headers"
          :sumHeaders="sumHeaders"
          hideHeader
          :loading="loading"
          :showSearch="false"
          showTotals
          :groupBy="!isSpeciesView ? 'woodType' : undefined">
            <template #group.header="{ group }">
              <span class="title">{{ woodTypeName(group) }}</span>
            </template>
            <template #group-total.productName="{ group }">
              {{ `${woodTypeName(group.group)} ${$t('totals')}` }}
            </template>
            <template #group.summary.prepend="{ group }">
              <p v-if="$vuetify.breakpoint.xs" class="text-subtitle-2 ml-4 mt-4 mb-2 font-weight-black">{{ `${woodTypeName(group)} ${$t('totals')}` }}</p>
            </template>
            <template #total.speciesName>
              {{ $t('totals') }}
            </template>
            <template #total.productName>
              {{ $t('totals') }}
            </template>
          </DataTable>
          <NoData v-else>
            {{ $t('noDataForSelection') }}
          </NoData>
        </LoadableComponent>
      </v-col>
    </v-row>
    <v-row v-if="analysisData && !loading" dense class="mt-2">
      <v-col cols="12" sm="6" md="4">
        <DetailCard :title="$t('tractPayments')">
          <v-row no-gutters>
            <v-col>{{ $t('totalPayments') }}:</v-col>
            <v-col cols="auto">{{ formatMoney(paymentSummary.total) }}</v-col>
          </v-row>
        </DetailCard>
      </v-col>
      <v-col cols="12" sm="6" md="4" v-if="showObligationCard">
        <DetailCard :title="$t('obligation')">
          <v-row no-gutters>
            <v-col>{{ $t('totalObligation') }}:</v-col>
            <v-col cols="auto">{{ formatMoney(tractGroup.cost) }}</v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>{{ $t('obligationAccrued') }}:</v-col>
            <v-col cols="auto">{{ formatMoney(obligationSummary.accrued) }}</v-col>
          </v-row>
          <v-row no-gutters>
            <v-col>{{ $t('obligationAdjustment') }}:</v-col>
            <v-col cols="auto">{{ formatMoney(obligationSummary.adjustment) }}</v-col>
          </v-row>
          <v-row no-gutters class="font-weight-bold">
            <v-col>{{ $t('obligationBalance') }}:</v-col>
            <v-col cols="auto">{{ formatMoney(tractGroup.balance) }}</v-col>
          </v-row>
        </DetailCard>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { saveAs } from 'file-saver'
import { TractTypeSpecialization, WoodType } from '@/utils/Enumerations'
import { tractGroupAnalysisHeaders } from '@/headers/Tract.js'
import { poundsToTons, formatMoney } from '@/utils/NumericMutations'
import { fileNameSafeString, appendDateToFileName } from '@/utils/Files'
import { generateCsvString } from '@/utils/CSVUtility'
import { generateTractGroupAnalysisReport } from '@/utils/pdf-formatters/TractGroupAnalysisReport.js'

const { ObligationUncapped, ObligationCapped } = TractTypeSpecialization

export default {
  name: 'TractGroupAnalysis',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    TabbedColumnAdjuster: () => import('@/components/helper/TabbedColumnAdjuster.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    NoData: () => import('@/components/core/NoData.vue')
  },

  props: {
    tractGroup: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    analysisData: null,
    loading: false,
    isSpeciesView: true,
    columnSet: null
  }),

  computed: {
    ...mapGetters('tract-type', ['allTractTypes']),

    tractType () {
      return this.allTractTypes.find(tt => tt.tractTypeId === this.tractGroup.tractTypeId)
    },

    isObligationTract () {
      const spec = this.tractType?.specialization
      return spec === ObligationUncapped.value || spec === ObligationCapped.value
    },

    isUncapped () {
      return this.tractType?.specialization === ObligationUncapped.value
    },

    analysisHeaders () {
      return tractGroupAnalysisHeaders(this.isSpeciesView, this.isUncapped)
    },

    columnAdjusterTabs () {
      return ['cruise', 'operational', 'financial']
    },

    headers () {
      if (!this.columnSet) return []
      return this.columnSet.getColumns()
    },

    sumHeaders () {
      return this.columnSet ? this.columnSet.getSummableHeaders() : []
    },

    tableItems () {
      if (!this.analysisData) return []
      const items = this.isSpeciesView
        ? this.analysisData.speciesAnalysis ?? []
        : this.analysisData.productAnalysis ?? []

      const converted = items.map(item => {
        const rawCruised = (this.isSpeciesView ? item.cruisedVolume : item.cruisedWeight) || 0
        const rawHarvested = (this.isSpeciesView ? item.harvestedVolume : item.harvestedWeight) || 0
        const cruisedVolume = this.isSpeciesView ? rawCruised / 1000 : poundsToTons(rawCruised)
        const harvestedVolume = this.isSpeciesView ? rawHarvested / 1000 : poundsToTons(rawHarvested)
        const cutoutPercentage = cruisedVolume > 0 ? (harvestedVolume / cruisedVolume) * 100 : 0
        const totalCost = (item.stumpageAmount ?? 0) + (item.productionAmount ?? 0)

        const computed = {
          ...item,
          cruisedVolume,
          harvestedVolume,
          cutoutPercentage,
          totalCost
        }

        if (this.isSpeciesView) {
          const obligationAccrued = item.obligationAccrued ?? 0
          const obligationAdjustment = item.obligationAdjustment ?? 0
          computed.adjustedObligationAccrued = obligationAccrued + obligationAdjustment

          if (this.isUncapped) {
            const totalObligation = item.totalObligation ?? 0
            computed.obligationRate = cruisedVolume > 0 ? totalObligation / cruisedVolume : 0
            computed.obligationBalance = totalObligation - obligationAccrued - obligationAdjustment
          }
        }

        return computed
      })

      const totalHarvested = converted.reduce((sum, item) => sum + item.harvestedVolume, 0)
      return converted.map(item => ({
        ...item,
        harvestedPercentage: totalHarvested > 0 ? (item.harvestedVolume / totalHarvested) * 100 : 0
      }))
    },

    paymentSummary () {
      return {
        total: this.analysisData?.totalTractPayments ?? 0
      }
    },

    obligationSummary () {
      const species = this.analysisData?.speciesAnalysis ?? []
      const total = species.reduce((s, i) => s + (i.totalObligation ?? 0), 0)
      const accrued = species.reduce((s, i) => s + (i.obligationAccrued ?? 0), 0)
      const adjustment = species.reduce((s, i) => s + (i.obligationAdjustment ?? 0), 0)
      return { total, accrued, adjustment, balance: total - accrued - adjustment }
    },

    showObligationCard () {
      return this.isSpeciesView && this.isObligationTract && !this.isUncapped && (this.analysisData?.speciesAnalysis?.length ?? 0) > 0
    },

    contextMenuOptions () {
      return [
        { label: 'downloadPDF', icon: 'mdi-file-pdf-box', action: this.downloadPDF },
        { label: 'downloadCSV', icon: 'mdi-file-delimited-outline', action: this.downloadCSV }
      ]
    }
  },

  watch: {
    isSpeciesView () {
      this.columnSet = this.analysisHeaders
    }
  },

  created () {
    this.columnSet = this.analysisHeaders
  },

  mounted () {
    this.refreshAnalysis()
  },

  methods: {
    formatMoney,
    ...mapActions('tract', ['fetchTractGroupAnalysis']),

    woodTypeName (e) {
      return WoodType.forInt(e)?.name
    },

    async refreshAnalysis () {
      if (this.loading) return
      this.loading = true
      try {
        this.analysisData = await this.fetchTractGroupAnalysis(this.tractGroup.tractGroupId)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    toggleView () {
      this.isSpeciesView = !this.isSpeciesView
    },

    downloadCSV () {
      if (!this.tableItems.length) return
      const columns = this.columnSet.columns
        .filter(c => !!c.text)
        .map(c => ({
          text: c.text,
          value: item => c.format ? c.format(item[c.value]) : item[c.value]
        }))
      const csvString = generateCsvString(this.tableItems, columns)
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      const fileName = appendDateToFileName(`Tract_Group_Analysis_${fileNameSafeString(this.tractGroup?.name || 'TractGroup')}`)
      saveAs(blob, `${fileName}.csv`)
    },

    downloadPDF () {
      if (!this.tableItems.length) return
      generateTractGroupAnalysisReport({
        tractGroup: this.tractGroup,
        items: this.tableItems,
        headers: this.columnSet.columns,
        sumHeaders: this.sumHeaders,
        isSpeciesView: this.isSpeciesView
      })
    }
  }
}
</script>
